import { useRef } from 'react';
import { motion } from 'framer-motion';
import { GripHorizontal } from 'lucide-react';
import { portfolioItems } from '../data/mock';
import { useScrollReveal } from '../hooks/useScrollReveal';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNavigation,
  CarouselIndicator,
} from './ui/carousel';

// ─── Shared card ─────────────────────────────────────────────────────────────
const PortfolioCard = ({ item }) => (
  <div className="relative w-full h-full rounded-2xl overflow-hidden">
    <img
      src={item.image}
      alt={item.title}
      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 hover:scale-105"
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = 'https://placehold.co/600x460/2d3748/ffffff?text=Image+Error';
      }}
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
    <div className="absolute bottom-0 left-0 w-full p-6 z-10">
      <span className="text-blue-400 text-xs font-semibold tracking-wider uppercase">
        {item.category}
      </span>
      <h3 className="text-white text-2xl font-bold mt-1 mb-3 leading-tight">
        {item.title}
      </h3>
      <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
        <span className="text-blue-300 text-sm font-bold">{item.result}</span>
      </div>
    </div>
  </div>
);

// ─── Main Section ────────────────────────────────────────────────────────────
export const PortfolioSection = () => {
  const [titleRef, titleVisible] = useScrollReveal();
  const dragRef = useRef(null);

  return (
    <section id="resultados" className="relative py-20 md:py-32 bg-white dark:bg-[#030B1A] overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-200 dark:via-blue-500/20 to-transparent" />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-8">

        {/* ══════════════════════════════════════════════════════════════
            DESKTOP: side-by-side — text left, one-card-at-a-time carousel right
        ══════════════════════════════════════════════════════════════ */}
        <div className="hidden md:flex items-center gap-12 lg:gap-16">

          {/* Left: text */}
          <div ref={titleRef} className="w-5/12 shrink-0 text-left">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={titleVisible ? { opacity: 1, y: 0 } : {}}
              className="inline-block text-blue-600 dark:text-blue-400 text-sm font-semibold tracking-widest uppercase mb-4"
            >
              Portfólio
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={titleVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 }}
              className="text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white leading-tight"
            >
              Resultados<br />
              que{' '}
              <span className="bg-gradient-to-r from-blue-600 to-blue-400 dark:from-blue-400 dark:to-blue-300 bg-clip-text text-transparent">
                falam
              </span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={titleVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 }}
              className="mt-6 text-lg text-slate-500 dark:text-slate-400 max-w-xs leading-relaxed"
            >
              Use as setas ou arraste para navegar pelos resultados que entregamos.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={titleVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4 }}
              className="mt-8"
            >
              <a
                href="#contato"
                className="inline-block px-8 py-3 rounded-xl bg-slate-900 dark:bg-blue-600 text-white font-semibold shadow-lg hover:bg-slate-800 dark:hover:bg-blue-500 transition-colors duration-300"
              >
                Fale Conosco
              </a>
            </motion.div>
          </div>

          {/* Right: one-card-at-a-time Carousel */}
          <div className="w-7/12">
            <Carousel className="w-full">
              <CarouselContent>
                {portfolioItems.map((item) => (
                  // basis-full = 100% width = one card visible at a time
                  <CarouselItem key={item.id} className="basis-full">
                    <div className="h-[440px] lg:h-[480px]">
                      <PortfolioCard item={item} />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              {/* Arrow buttons always visible */}
              <CarouselNavigation
                alwaysShow
                classNameButton="bg-blue-600 hover:bg-blue-500 dark:bg-blue-600 p-2.5 rounded-full shadow-lg transition-colors duration-200 disabled:opacity-30 [&_svg]:stroke-white"
              />
              <CarouselIndicator className="static mt-6 justify-start" />
            </Carousel>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════
            MOBILE: stacked — text top, free-drag carousel below
        ══════════════════════════════════════════════════════════════ */}
        <div className="block md:hidden">
          {/* Text */}
          <div className="text-center mb-8">
            <span className="inline-block text-blue-600 dark:text-blue-400 text-sm font-semibold tracking-widest uppercase mb-3">
              Portfólio
            </span>
            <h2 className="text-4xl font-bold text-slate-900 dark:text-white leading-tight">
              Resultados<br />
              que{' '}
              <span className="bg-gradient-to-r from-blue-600 to-blue-400 dark:from-blue-400 dark:to-blue-300 bg-clip-text text-transparent">
                falam
              </span>
            </h2>
            <p className="mt-4 text-sm text-slate-500 dark:text-slate-400 flex items-center justify-center gap-1.5">
              <GripHorizontal className="w-4 h-4 opacity-60" />
              Arraste para ver as transformações que entregamos.
            </p>
          </div>

          {/* Free-drag carousel */}
          <motion.div
            ref={dragRef}
            className="flex gap-4 pb-4 cursor-grab active:cursor-grabbing select-none overflow-visible"
            drag="x"
            dragConstraints={{ left: -(portfolioItems.length * 250), right: 20 }}
            dragElastic={0.06}
            whileTap={{ cursor: 'grabbing' }}
            style={{ touchAction: 'pan-x' }}
          >
            {portfolioItems.map((item) => (
              <div
                key={item.id}
                className="relative w-[75vw] h-[360px] rounded-2xl overflow-hidden flex-shrink-0"
              >
                <PortfolioCard item={item} />
              </div>
            ))}
          </motion.div>

          {/* Dot indicators */}
          <div className="flex justify-center gap-2 mt-4">
            {portfolioItems.map((_, i) => (
              <div key={i} className="w-1.5 h-1.5 rounded-full bg-blue-400/40" />
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-8">
            <a href="#contato" className="inline-block px-8 py-3 rounded-xl bg-slate-900 dark:bg-blue-600 text-white font-semibold shadow-lg hover:bg-slate-800 dark:hover:bg-blue-500 transition-colors duration-300">
              Fale Conosco
            </a>
          </div>
        </div>

      </div>
    </section>
  );
};
